import { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import {
  SuperAdminNotificationRecipient,
  SuperAdminNotificationRecipientCreateInput,
  SuperAdminNotificationRecipientUpdateInput,
} from '@models/superAdminNotificationRecipient.model';

interface CreateEditRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: SuperAdminNotificationRecipientCreateInput) => Promise<void> | void;
  onUpdate: (data: SuperAdminNotificationRecipientUpdateInput) => Promise<void> | void;
  saving: boolean;
  editing: boolean;
  initialData?: SuperAdminNotificationRecipient | null;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .required('Email is required')
    .email('Invalid email format')
    .max(255, 'Email is too long'),
  active: Yup.boolean().required(),
  notes: Yup.string().max(500, 'Notes must be 500 characters or fewer').nullable(),
});

const CreateEditRecipientModal: FC<CreateEditRecipientModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  saving,
  editing,
  initialData,
}) => {
  const formik = useFormik({
    initialValues: {
      email: initialData?.email || '',
      active: initialData?.active ?? true,
      notes: initialData?.notes || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const trimmedNotes = values.notes.trim();
      const basePayload = {
        email: values.email.trim().toLowerCase(),
        active: values.active,
        notes: trimmedNotes ? trimmedNotes : null,
      };
      if (editing && initialData) {
        await onUpdate({ id: initialData.id, ...basePayload });
      } else {
        await onCreate(basePayload);
      }
    },
  });

  const { values, errors, touched, handleChange, submitForm, resetForm, setFieldValue } = formik;

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={saving}
      handleConfirm={submitForm}
      title={editing ? 'Edit Notification Recipient' : 'Add Notification Recipient'}
      confirmText={editing ? 'Save changes' : 'Add recipient'}
      size="medium">
      <form className="flex flex-col gap-6">
        <FormGroup
          error={touched.email && errors.email ? errors.email : undefined}
          htmlFor="email"
          label="Email">
          <Input
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            placeholder="recipient@example.com"
            error={!!(touched.email && errors.email)}
          />
        </FormGroup>

        <FormGroup htmlFor="active" label="Active">
          <div className="flex flex-col gap-2">
            <ToggleSwitch
              leftLabel="Disabled"
              rightLabel="Active"
              checked={values.active}
              onChange={(checked: boolean) => setFieldValue('active', checked)}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {values.active
                ? 'This recipient will receive notifications.'
                : 'This recipient is disabled and will not receive notifications.'}
            </span>
          </div>
        </FormGroup>

        <FormGroup
          error={touched.notes && errors.notes ? errors.notes : undefined}
          htmlFor="notes"
          label="Notes (optional)">
          <Input
            name="notes"
            value={values.notes}
            onChange={handleChange}
            placeholder="e.g., Edwin, Product"
            error={!!(touched.notes && errors.notes)}
          />
        </FormGroup>
      </form>
    </Modal>
  );
};

export default CreateEditRecipientModal;
